import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckCircle, FiEdit2, FiLayers, FiLink, FiPackage, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import axios from "../../api/axiosInstance";

const PRODUCT_PICKER_PAGE_SIZE = 50;

const LANGUAGE_LABELS = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  nl: "Dutch",
  ca: "Catalan",
};

function translationsForLanguages(languages, source = {}) {
  return Object.fromEntries(languages.map((language) => [language, source?.[language] || ""]));
}

function translatedName(translations, language, fallbackLanguage, fallback = "") {
  return translations?.[language]
    || translations?.[fallbackLanguage]
    || Object.values(translations || {}).find(Boolean)
    || fallback;
}

export default function CatalogMenuManagerModal({ catalog, businessUnitId, onClose, onChanged }) {
  const [menuCatalog, setMenuCatalog] = useState(catalog);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState("items");
  const [displayLanguage, setDisplayLanguage] = useState(catalog.guest_default_language || "en");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [categoryEditor, setCategoryEditor] = useState(null);
  const [itemEditor, setItemEditor] = useState(null);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerQuery, setProductPickerQuery] = useState("");
  const [debouncedProductPickerQuery, setDebouncedProductPickerQuery] = useState("");
  const [productPickerRows, setProductPickerRows] = useState([]);
  const [productPickerCount, setProductPickerCount] = useState(0);
  const [productPickerPage, setProductPickerPage] = useState(0);
  const [productPickerLoading, setProductPickerLoading] = useState(false);
  const [productPickerError, setProductPickerError] = useState("");
  const [pendingPosProduct, setPendingPosProduct] = useState(null);

  const loadMenu = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`/api/pos/catalogs/${catalog.id}/menu`, {
        params: { business_unit_id: businessUnitId },
      });
      const nextCatalog = res.data?.catalog || catalog;
      const languages = nextCatalog.guest_enabled_languages?.length
        ? nextCatalog.guest_enabled_languages
        : [nextCatalog.guest_default_language || "en"];
      setMenuCatalog(nextCatalog);
      setCategories(res.data?.categories || []);
      setItems(res.data?.items || []);
      setDisplayLanguage((current) => languages.includes(current) ? current : nextCatalog.guest_default_language || languages[0]);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load the guest menu.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, catalog]);

  useEffect(() => {
    loadMenu();
  }, [loadMenu]);

  useEffect(() => {
    if (!productPickerOpen) return undefined;
    const timer = window.setTimeout(() => {
      setDebouncedProductPickerQuery(productPickerQuery.trim());
      setProductPickerPage(0);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [productPickerOpen, productPickerQuery]);

  const loadProductPickerRows = useCallback(async () => {
    if (!productPickerOpen) return;
    try {
      setProductPickerLoading(true);
      setProductPickerError("");
      const res = await axios.get("/api/pos/products/options", {
        params: {
          business_unit_id: businessUnitId,
          q: debouncedProductPickerQuery || undefined,
          limit: PRODUCT_PICKER_PAGE_SIZE,
          offset: productPickerPage * PRODUCT_PICKER_PAGE_SIZE,
        },
      });
      setProductPickerRows(res.data?.rows || []);
      setProductPickerCount(Number(res.data?.count || 0));
    } catch (err) {
      setProductPickerError(err?.response?.data?.error || "Could not load POS products.");
    } finally {
      setProductPickerLoading(false);
    }
  }, [businessUnitId, debouncedProductPickerQuery, productPickerOpen, productPickerPage]);

  useEffect(() => {
    loadProductPickerRows();
  }, [loadProductPickerRows]);

  const languages = useMemo(() => {
    const enabled = menuCatalog?.guest_enabled_languages;
    return enabled?.length ? enabled : [menuCatalog?.guest_default_language || "en"];
  }, [menuCatalog]);
  const defaultLanguage = menuCatalog?.guest_default_language || languages[0] || "en";
  const productPickerPageCount = Math.max(Math.ceil(productPickerCount / PRODUCT_PICKER_PAGE_SIZE), 1);
  const selectedItemCategory = categories.find((category) => (
    String(category.id) === String(itemEditor?.category_id || "")
  )) || null;

  const openCategoryEditor = (category = null) => {
    setError("");
    setMessage("");
    setCategoryEditor({
      id: category?.id || null,
      name_translations: translationsForLanguages(languages, category?.name_translations),
      display_order: category?.display_order ?? categories.length,
      is_active: category?.is_active !== false,
    });
  };

  const openItemEditor = (item = null) => {
    setError("");
    setMessage("");
    setItemEditor({
      id: item?.id || null,
      pos_product_id: item?.pos_product_id ? String(item.pos_product_id) : "",
      pos_product: item?.pos_product || null,
      category_id: item?.category_id ? String(item.category_id) : "",
      name_translations: translationsForLanguages(languages, item?.name_translations),
      description_translations: translationsForLanguages(languages, item?.description_translations),
      display_order: item?.display_order ?? items.length,
      price_override: item?.price_override ?? "",
      is_available: item?.is_available !== false,
    });
  };

  const openProductPicker = () => {
    setProductPickerQuery("");
    setDebouncedProductPickerQuery("");
    setProductPickerPage(0);
    setProductPickerRows([]);
    setProductPickerCount(0);
    setProductPickerError("");
    setPendingPosProduct(itemEditor?.pos_product || null);
    setProductPickerOpen(true);
  };

  const confirmProductSelection = () => {
    if (!pendingPosProduct) return;
    setItemEditor((current) => ({
      ...current,
      pos_product_id: String(pendingPosProduct.id),
      pos_product: pendingPosProduct,
    }));
    setProductPickerOpen(false);
  };

  const saveCategory = async () => {
    if (!categoryEditor) return;
    if (!String(categoryEditor.name_translations?.[defaultLanguage] || "").trim()) {
      setError(`Enter the ${LANGUAGE_LABELS[defaultLanguage] || defaultLanguage.toUpperCase()} category name.`);
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        business_unit_id: businessUnitId,
        name_translations: categoryEditor.name_translations,
        display_order: Number(categoryEditor.display_order || 0),
        is_active: categoryEditor.is_active,
      };
      if (categoryEditor.id) {
        await axios.patch(`/api/pos/catalogs/${catalog.id}/menu/categories/${categoryEditor.id}`, payload);
      } else {
        await axios.post(`/api/pos/catalogs/${catalog.id}/menu/categories`, payload);
      }
      setCategoryEditor(null);
      setMessage(categoryEditor.id ? "Guest category updated." : "Guest category created.");
      await loadMenu();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save the guest category.");
    } finally {
      setSaving(false);
    }
  };

  const saveItem = async () => {
    if (!itemEditor) return;
    if (!itemEditor.pos_product_id) {
      setError("Select the linked POS product.");
      return;
    }
    if (!String(itemEditor.name_translations?.[defaultLanguage] || "").trim()) {
      setError(`Enter the ${LANGUAGE_LABELS[defaultLanguage] || defaultLanguage.toUpperCase()} guest product name.`);
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        business_unit_id: businessUnitId,
        pos_product_id: itemEditor.pos_product_id,
        category_id: itemEditor.category_id || null,
        name_translations: itemEditor.name_translations,
        description_translations: itemEditor.description_translations,
        display_order: Number(itemEditor.display_order || 0),
        price_override: itemEditor.price_override === "" ? null : itemEditor.price_override,
        is_available: itemEditor.is_available,
      };
      if (itemEditor.id) {
        await axios.patch(`/api/pos/catalogs/${catalog.id}/menu/items/${itemEditor.id}`, payload);
      } else {
        await axios.post(`/api/pos/catalogs/${catalog.id}/menu/items`, payload);
      }
      setItemEditor(null);
      setMessage(itemEditor.id ? "Guest product updated." : "Guest product created.");
      await loadMenu();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save the guest product.");
    } finally {
      setSaving(false);
    }
  };

  const removeCategory = async (category) => {
    if (!window.confirm("Delete this guest category? Its products will remain unassigned.")) return;
    try {
      setSaving(true);
      setError("");
      await axios.delete(`/api/pos/catalogs/${catalog.id}/menu/categories/${category.id}`, {
        data: { business_unit_id: businessUnitId },
      });
      setMessage("Guest category deleted.");
      await loadMenu();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not delete the guest category.");
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm("Remove this product from the guest catalog?")) return;
    try {
      setSaving(true);
      setError("");
      await axios.delete(`/api/pos/catalogs/${catalog.id}/menu/items/${item.id}`, {
        data: { business_unit_id: businessUnitId },
      });
      setMessage("Guest product removed.");
      await loadMenu();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not remove the guest product.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="posModalOverlay" role="presentation">
      <div className="posTicketModal posCatalogMenuModal" role="dialog" aria-modal="true" aria-label="Guest catalog menu">
        <div className="posTicketModalHeader">
          <div>
            <h3 className="rm-rates-title">{catalog.name}</h3>
            <div className="rm-rates-subtitle">Guest-facing menu linked to POS products</div>
          </div>
          <button type="button" className="res-filterCloseBtn" onClick={onClose} disabled={saving} aria-label="Close">x</button>
        </div>

        {error ? <div className="posStateBanner">{error}</div> : null}
        {message ? <div className="posInfoBanner">{message}</div> : null}

        <div className="posCatalogMenuToolbar">
          <div className="posCatalogMenuTabs" role="tablist" aria-label="Guest catalog sections">
            <button type="button" className={activeTab === "items" ? "posCatalogMenuTab--active" : ""} onClick={() => setActiveTab("items")}><FiPackage /> Guest products</button>
            <button type="button" className={activeTab === "categories" ? "posCatalogMenuTab--active" : ""} onClick={() => setActiveTab("categories")}><FiLayers /> Guest categories</button>
          </div>
          <div className="posCatalogMenuActions">
            <label>
              <span>Preview language</span>
              <select className="posSelect" value={displayLanguage} onChange={(event) => setDisplayLanguage(event.target.value)}>
                {languages.map((language) => <option key={language} value={language}>{LANGUAGE_LABELS[language] || language.toUpperCase()}</option>)}
              </select>
            </label>
            <button type="button" className="rm-topBtn rm-topBtn--green" onClick={() => activeTab === "items" ? openItemEditor() : openCategoryEditor()}>
              <FiPlus /> {activeTab === "items" ? "Add guest product" : "Add category"}
            </button>
          </div>
        </div>

        <div className="posCatalogMenuTableWrap">
          {loading ? <div className="posCatalogMenuState">Loading guest menu...</div> : activeTab === "items" ? (
            <table className="rm-rates-table" style={{ "--rm-col1-width": "260px", "--rm-table-min-width": "980px" }}>
              <thead><tr>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Guest name</span><span className="res-thSub">{LANGUAGE_LABELS[displayLanguage] || displayLanguage}</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Guest category</span><span className="res-thSub">Menu navigation</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Linked POS product</span><span className="res-thSub">Infrasys name</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Price</span><span className="res-thSub">Override / POS</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Tablet</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Actions</span><span className="res-thSub">Edit menu</span></div></th>
              </tr></thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <span className="posCatalogGuestName">
                        <strong className="posDeviceName">{translatedName(item.name_translations, displayLanguage, defaultLanguage, "Unnamed product")}</strong>
                        <small>{translatedName(
                          item.description_translations,
                          displayLanguage,
                          defaultLanguage,
                          item.category ? translatedName(item.category.name_translations, displayLanguage, defaultLanguage, "") : ""
                        )}</small>
                      </span>
                    </td>
                    <td>{item.category ? translatedName(item.category.name_translations, displayLanguage, defaultLanguage, "Unnamed category") : "-"}</td>
                    <td><span className="posCatalogPosLink"><FiLink /><span><strong>{item.pos_product?.name || "-"}</strong><small>{item.pos_product?.plu || "No PLU"}</small></span></span></td>
                    <td>{Number(item.unit_price || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</td>
                    <td><span className={`rm-chip ${item.is_available ? "rm-chip--ok" : "rm-chip--off"}`}>{item.is_available ? "Visible" : "Hidden"}</span></td>
                    <td><div className="posDeviceActions"><button type="button" className="rm-topBtn" onClick={() => openItemEditor(item)}><FiEdit2 /> Edit</button><button type="button" className="rm-topBtn rm-topBtn--amber" onClick={() => removeItem(item)} disabled={saving}><FiTrash2 /> Remove</button></div></td>
                  </tr>
                ))}
                {!items.length ? <tr><td colSpan="6" className="posMuted">No guest products yet.</td></tr> : null}
              </tbody>
            </table>
          ) : (
            <table className="rm-rates-table" style={{ "--rm-col1-width": "280px", "--rm-table-min-width": "760px" }}>
              <thead><tr>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Guest category</span><span className="res-thSub">{LANGUAGE_LABELS[displayLanguage] || displayLanguage}</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Translations</span><span className="res-thSub">Completed</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Products</span><span className="res-thSub">Assigned</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Order</span><span className="res-thSub">Position</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Tablet</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Actions</span><span className="res-thSub">Edit menu</span></div></th>
              </tr></thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id}>
                    <td><strong className="posDeviceName">{translatedName(category.name_translations, displayLanguage, defaultLanguage, "Unnamed category")}</strong></td>
                    <td>{Object.values(category.name_translations || {}).filter(Boolean).length}/{languages.length}</td>
                    <td>{Number(category.item_count || 0).toLocaleString("es-ES")}</td>
                    <td>{Number(category.display_order || 0).toLocaleString("es-ES")}</td>
                    <td><span className={`rm-chip ${category.is_active ? "rm-chip--ok" : "rm-chip--off"}`}>{category.is_active ? "Visible" : "Hidden"}</span></td>
                    <td><div className="posDeviceActions"><button type="button" className="rm-topBtn" onClick={() => openCategoryEditor(category)}><FiEdit2 /> Edit</button><button type="button" className="rm-topBtn rm-topBtn--amber" onClick={() => removeCategory(category)} disabled={saving}><FiTrash2 /> Delete</button></div></td>
                  </tr>
                ))}
                {!categories.length ? <tr><td colSpan="6" className="posMuted">No guest categories yet.</td></tr> : null}
              </tbody>
            </table>
          )}
        </div>

        <div className="posProductModalFooter">
          <button type="button" className="rm-topBtn" onClick={onClose} disabled={saving}>Close</button>
        </div>

        {categoryEditor ? (
          <div className="posNestedModalOverlay" role="presentation">
            <div className="posTicketModal posCatalogEditorModal" role="dialog" aria-modal="true" aria-label="Guest category editor">
              <div className="posTicketModalHeader"><div><h3 className="rm-rates-title">{categoryEditor.id ? "Edit guest category" : "Add guest category"}</h3><div className="rm-rates-subtitle">Names shown in the tablet navigation</div></div><button type="button" className="res-filterCloseBtn" onClick={() => setCategoryEditor(null)} disabled={saving} aria-label="Close">x</button></div>
              {error ? <div className="posStateBanner">{error}</div> : null}
              <div className="posProductModalBody">
                <div className="posCatalogTranslationGrid">
                  {languages.map((language) => <label className="posModalField" key={language}><span>{LANGUAGE_LABELS[language] || language.toUpperCase()}{language === defaultLanguage ? " · Default" : ""}</span><input className="posTextInput" value={categoryEditor.name_translations[language] || ""} onChange={(event) => setCategoryEditor((current) => ({ ...current, name_translations: { ...current.name_translations, [language]: event.target.value } }))} /></label>)}
                </div>
                <div className="posCatalogEditorRow"><label className="posModalField"><span>Display order</span><input className="posTextInput" type="number" min="0" value={categoryEditor.display_order} onChange={(event) => setCategoryEditor((current) => ({ ...current, display_order: event.target.value }))} /></label><label className="posCatalogToggle"><input type="checkbox" checked={categoryEditor.is_active} onChange={(event) => setCategoryEditor((current) => ({ ...current, is_active: event.target.checked }))} /><span>Visible on tablet</span></label></div>
              </div>
              <div className="posProductModalFooter"><button type="button" className="rm-topBtn" onClick={() => setCategoryEditor(null)} disabled={saving}>Cancel</button><button type="button" className="rm-topBtn rm-topBtn--green" onClick={saveCategory} disabled={saving}>{saving ? "Saving" : "Save category"}</button></div>
            </div>
          </div>
        ) : null}

        {itemEditor ? (
          <div className="posNestedModalOverlay" role="presentation">
            <div className="posTicketModal posCatalogEditorModal" role="dialog" aria-modal="true" aria-label="Guest product editor">
              <div className="posTicketModalHeader"><div><h3 className="rm-rates-title">{itemEditor.id ? "Edit guest product" : "Add guest product"}</h3><div className="rm-rates-subtitle">Guest content linked to the operational POS item</div></div><button type="button" className="res-filterCloseBtn" onClick={() => setItemEditor(null)} disabled={saving} aria-label="Close">x</button></div>
              {error ? <div className="posStateBanner">{error}</div> : null}
              <div className="posProductModalBody">
                <div className="posCatalogLinkFields">
                  <div className="posModalField">
                    <span>Linked POS product</span>
                    <div className={`posCatalogLinkedProduct${itemEditor.pos_product ? "" : " posCatalogLinkedProduct--empty"}`}>
                      <FiLink />
                      <span className="posCatalogLinkedProductText">
                        <strong>{itemEditor.pos_product?.name || "No POS product linked"}</strong>
                        <small>{itemEditor.pos_product?.plu ? `PLU ${itemEditor.pos_product.plu}` : "Choose an Infrasys product"}</small>
                      </span>
                      <button type="button" className="rm-topBtn" onClick={openProductPicker}>
                        <FiSearch /> {itemEditor.pos_product ? "Change" : "Choose product"}
                      </button>
                    </div>
                  </div>
                  <label className="posModalField"><span>Guest category</span><select className="posSelect" value={itemEditor.category_id} onChange={(event) => setItemEditor((current) => ({ ...current, category_id: event.target.value }))}><option value="">Unassigned</option>{categories.map((category) => <option key={category.id} value={category.id}>{translatedName(category.name_translations, defaultLanguage, defaultLanguage, "Unnamed category")}</option>)}</select></label>
                </div>
                <div className="posCatalogTranslationGrid">
                  {languages.map((language) => <label className="posModalField" key={language}><span>{LANGUAGE_LABELS[language] || language.toUpperCase()}{language === defaultLanguage ? " · Default" : ""}</span><input className="posTextInput" value={itemEditor.name_translations[language] || ""} onChange={(event) => setItemEditor((current) => ({ ...current, name_translations: { ...current.name_translations, [language]: event.target.value } }))} placeholder="Guest-facing product name" /></label>)}
                </div>
                <div className="posCatalogTranslationGrid posCatalogSubtitleGrid">
                  {languages.map((language) => (
                    <label className="posModalField" key={language}>
                      <span>Card subtitle · {LANGUAGE_LABELS[language] || language.toUpperCase()}</span>
                      <input
                        className="posTextInput"
                        value={itemEditor.description_translations[language] || ""}
                        onChange={(event) => setItemEditor((current) => ({
                          ...current,
                          description_translations: {
                            ...current.description_translations,
                            [language]: event.target.value,
                          },
                        }))}
                        placeholder={selectedItemCategory
                          ? translatedName(selectedItemCategory.name_translations, language, defaultLanguage, "")
                          : "Optional subtitle"}
                        maxLength={500}
                      />
                    </label>
                  ))}
                </div>
                <div className="posCatalogEditorRow"><label className="posModalField"><span>Price override</span><input className="posTextInput" type="number" min="0" step="0.01" value={itemEditor.price_override} onChange={(event) => setItemEditor((current) => ({ ...current, price_override: event.target.value }))} placeholder="Use latest POS price" /></label><label className="posModalField"><span>Display order</span><input className="posTextInput" type="number" min="0" value={itemEditor.display_order} onChange={(event) => setItemEditor((current) => ({ ...current, display_order: event.target.value }))} /></label><label className="posCatalogToggle"><input type="checkbox" checked={itemEditor.is_available} onChange={(event) => setItemEditor((current) => ({ ...current, is_available: event.target.checked }))} /><span>Visible on tablet</span></label></div>
              </div>
              <div className="posProductModalFooter"><button type="button" className="rm-topBtn" onClick={() => setItemEditor(null)} disabled={saving}>Cancel</button><button type="button" className="rm-topBtn rm-topBtn--green" onClick={saveItem} disabled={saving}>{saving ? "Saving" : "Save guest product"}</button></div>
            </div>

            {productPickerOpen ? (
              <div className="posNestedModalOverlay posProductPickerOverlay" role="presentation">
                <div className="posTicketModal posProductPickerModal" role="dialog" aria-modal="true" aria-label="Select POS product">
                  <div className="posTicketModalHeader">
                    <div><h3 className="rm-rates-title">Select POS product</h3><div className="rm-rates-subtitle">Search the Infrasys catalog by product name or PLU</div></div>
                    <button type="button" className="res-filterCloseBtn" onClick={() => setProductPickerOpen(false)} aria-label="Close">x</button>
                  </div>
                  <div className="posProductPickerBody">
                    <label className="posModalField">
                      <span>Search</span>
                      <div className="posProductPickerSearch">
                        <FiSearch aria-hidden="true" />
                        <input
                          className="posTextInput"
                          value={productPickerQuery}
                          onChange={(event) => setProductPickerQuery(event.target.value)}
                          placeholder="Product name or PLU"
                          autoFocus
                        />
                      </div>
                    </label>
                    {productPickerError ? <div className="posStateBanner">{productPickerError}</div> : null}
                    <div className="res-listMetaRow posProductPickerMeta">
                      <span>
                        {productPickerLoading
                          ? "Searching products..."
                          : `${productPickerCount.toLocaleString("es-ES")} products found`}
                      </span>
                      <div className="posDeviceActions">
                        <button type="button" className="rm-topBtn" onClick={() => setProductPickerPage((page) => Math.max(page - 1, 0))} disabled={productPickerLoading || productPickerPage === 0}>Previous</button>
                        <span>{productPickerPage + 1} / {productPickerPageCount}</span>
                        <button type="button" className="rm-topBtn" onClick={() => setProductPickerPage((page) => Math.min(page + 1, productPickerPageCount - 1))} disabled={productPickerLoading || productPickerPage + 1 >= productPickerPageCount}>Next</button>
                      </div>
                    </div>
                    <div className="posProductPickerTableWrap">
                      <table className="rm-rates-table" style={{ "--rm-col1-width": "330px", "--rm-table-min-width": "680px" }}>
                        <thead><tr>
                          <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">POS product</span><span className="res-thSub">Infrasys name</span></div></th>
                          <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">PLU</span><span className="res-thSub">Source code</span></div></th>
                          <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Selection</span><span className="res-thSub">Catalog link</span></div></th>
                        </tr></thead>
                        <tbody>
                          {productPickerRows.map((product) => {
                            const selected = String(pendingPosProduct?.id || "") === String(product.id);
                            return (
                              <tr key={product.id} className={selected ? "posProductPickerRow--selected" : ""}>
                                <td><strong className="posDeviceName">{product.name}</strong></td>
                                <td>{product.plu || "-"}</td>
                                <td><button type="button" className={`rm-topBtn${selected ? " rm-topBtn--green" : ""}`} onClick={(event) => { event.stopPropagation(); setPendingPosProduct(product); }}><FiCheckCircle /> {selected ? "Selected" : "Select"}</button></td>
                              </tr>
                            );
                          })}
                          {!productPickerLoading && !productPickerRows.length ? <tr><td colSpan="3" className="posMuted">No POS products match this search.</td></tr> : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="posProductModalFooter">
                    <button type="button" className="rm-topBtn" onClick={() => setProductPickerOpen(false)}>Cancel</button>
                    <button type="button" className="rm-topBtn rm-topBtn--green" onClick={confirmProductSelection} disabled={!pendingPosProduct}><FiLink /> Link product</button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
